import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Checkbox, Divider, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { CypressIds } from '../../../constants/cypressIds';
import { CertSubmissionDuskIds } from '../../../constants/duskIds';
import { hideModal } from '../../../redux/modules/modal';
import colors from '../../../styles/theme/foundation/colors.js';
import CertificateCreationBlueprintType from '../../../types/CertificateCreationBlueprintType';
import InputLabel from '../../form/InputLabel';
import SearchableDropdownMenu from '../../SearchableDropdownMenu';

import DocumentViewer from './DocumentViewer';

const ReviewDocumentModal = ({ blueprint, file }) => {
  const dispatch = useDispatch();
  const documentRequirements = useSelector(blueprint.selectDocumentRequirements);
  const minimumRequirements = useSelector(blueprint.selectMinimumRequirements);
  const additionalPolicyTypes = useSelector(blueprint.selectAdditionalPolicyTypes);
  const documentTypes = useSelector(blueprint.selectInsuranceDocumentTypes);
  const { ocr: ocrEnabled } = useSelector(blueprint.selectOptions);
  const [checkedDocTypeIds, setCheckedDocTypeIds] = useState([]);
  const [checkedPolicyTypeIds, setCheckedPolicyTypeIds] = useState([]);
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [otherInputValue, setOtherInputValue] = useState('');
  const [docTypeError, setDocTypeError] = useState(null);
  const [showAdditionalPolicies, setShowAdditionalPolicies] = useState(false);

  useEffect(() => {
    dispatch(blueprint.getDocumentTypes({ deleted: true }));
  }, []);

  const { handleSubmit, reset, unregister, getValues, ...useFormMethods } = useForm();

  const validateInsuranceDocumentTypeName = name => {
    if (name.length > 64) {
      return { valid: false, message: 'Document Types must have fewer than 64 characters' };
    }

    if (name.length < 2) {
      return { valid: false, message: 'Document Types must have at least 2 characters' };
    }

    return { valid: true, message: '' };
  };

  const updateLinkedDocTypeIds = (docTypeId, checked) => {
    if (!checked) {
      setCheckedDocTypeIds(checkedDocTypeIds.filter(i => i !== docTypeId));
    } else {
      setCheckedDocTypeIds([...checkedDocTypeIds, docTypeId]);

      if (docTypeError) {
        setDocTypeError(null);
      }
    }
  };

  const onCustomInsuranceDocumentTypeCreate = async docTypeName => {
    const docTypeData = {
      name: docTypeName,
      display_name: docTypeName,
      description: ''
    };
    const docType = await dispatch(blueprint.createInsuranceDocumentType(docTypeData));
    setOtherInputValue(docType);
    useFormMethods.setValue('other_document_type', docType);
  };

  const updateLinkedPolicyTypes = (policyTypeId, checked) => {
    if (!checked) {
      setCheckedPolicyTypeIds(checkedPolicyTypeIds.filter(i => i !== policyTypeId));
    } else {
      setCheckedPolicyTypeIds([...checkedPolicyTypeIds, policyTypeId]);
    }
  };

  const handleOtherCheckboxClick = e => {
    updateLinkedDocTypeIds('other', e.target.checked);

    if (e.target.checked) {
      setShowOtherInput(true);

      if (docTypeError && !Object.keys(otherInputValue).length) {
        setDocTypeError(null);
      }
    } else {
      setShowOtherInput(false);
    }
  };

  return (
    <Box minH="750px">
      <form>
        <Flex color={colors.greyScale['900']} minH="750px" minW="100%">
          {/* Left Side */}
          <Box
            flexBasis="40%"
            pb={7}
            pt="2.75rem"
            px={7}
            textAlign="left"
          >
            <Box>
              <Text textStyle="displayXs">Review Document</Text>
              <Text
                color={colors.greyScale['500']}
                textStyle="bodyMd500"
              >
                Select all requirements that are included in this document.
              </Text>
            </Box>

            <Box mt={8}>
              <Text textStyle="bodyXl600">Required Documents</Text>
              <Divider borderBottomWidth="3px" mt={2} />

              <Box maxH="175px" overflowY="scroll">
                {documentRequirements.map(docReq => {
                  const id = docReq.insuranceDocumentTypeId;
                  return (
                    <Box key={id} my={4}>
                      <Checkbox
                        data-cy={CypressIds.certDocTypeCheckbox(id)}
                        dusk={CertSubmissionDuskIds.getDocumentUploadReqDocCheckboxByDocTypeId(id)}
                        value={id}
                        onChange={e => updateLinkedDocTypeIds(docReq.insuranceDocumentTypeId, e.target.checked)}
                      >
                        {docReq.insuranceDocumentTypeDisplayName}
                      </Checkbox>
                    </Box>
                  );
                })}

                <Box>
                  <Checkbox
                    value="other"
                    onChange={e => handleOtherCheckboxClick(e)}
                  >
                    Other
                  </Checkbox>
                </Box>
                {showOtherInput && (
                  <Box mt={2}>
                    <Text mb={2} textStyle="bodySm">
                      Only the above document types are being requested.
                      What type of document are you trying to upload?
                    </Text>
                    <InputLabel labelName="Other Document Type" />
                    <Controller
                      control={useFormMethods.control}
                      name="other_document_type"
                      render={({ field }) => (
                        <SearchableDropdownMenu
                          allowCustomValues
                          customLabel="New Document Type"
                          customPlaceholder="Document Type"
                          customValidation={validateInsuranceDocumentTypeName}
                          data={documentTypes}
                          fetchNextData={() => {}}
                          hasMore={false}
                          idKey="id"
                          labelKey="displayName"
                          maxWidth="400px"
                          minWidth="200px"
                          searchTerm={documentTypes.searchFilter}
                          selected={otherInputValue.displayName}
                          showError={useFormMethods.formState?.errors?.other_document_type}
                          truncateMenuItems={false}
                          variant="primary"
                          width="100%"
                          onCustomSelect={onCustomInsuranceDocumentTypeCreate}
                          onSearchChange={searchTerm => {
                            dispatch(blueprint.getDocumentTypes({ display_name: searchTerm, deleted: true }));
                          }}
                          onSelect={selected => {
                            field.onChange(selected);
                            setOtherInputValue(selected);
                          }}
                          {...field}
                        />
                      )}
                      rules={{ required: { value: showOtherInput, message: 'Please select an Other Document Type' } }}
                    />
                  </Box>
                )}
                {docTypeError && (
                  <Text color={colors.error.primary['600']} mb={2} textStyle="bodySm">
                    {docTypeError}
                  </Text>
                )}
              </Box>
            </Box>

            <Box>
              <Text mt={8} textStyle="bodyXl600">Required Policies</Text>
              <Divider borderBottomWidth="3px" mt={2} />

              <Box maxH="175px" overflowY="scroll">
                {Object.values(minimumRequirements).map(requirement => {
                  const id = requirement.policyTypeId;
                  return (
                    <Box key={id} my={4}>
                      <Checkbox
                        data-cy={CypressIds.certDocPolicyLinkCheckbox(id)}
                        dusk={CertSubmissionDuskIds.getDocumentUploadReqPolicyCheckboxByPolicyTypeId(id)}
                        value={id}
                        onChange={e => updateLinkedPolicyTypes(requirement.policyTypeId, e.target.checked)}
                      >
                        {requirement.policyTypeDisplayName}
                      </Checkbox>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box>
              <HStack alignItems="center" justifyContent="space-between" mb={2} mt={8}>
                <Text textStyle="bodyXl600">Add Additional Policies?</Text>
                <Button
                  data-cy={CypressIds.certDocAddAdditionalPoliciesButton}
                  textTransform="none"
                  variant="secondary"
                  onClick={() => setShowAdditionalPolicies(!showAdditionalPolicies)}
                >
                  {showAdditionalPolicies ? 'Cancel' : 'Add'}
                </Button>

              </HStack>

              <Text
                color={colors.greyScale['500']}
                textStyle="bodyMd500"
              >
                These are policies that are not being requested, but can still be submitted to support the requirements.
              </Text>

              {showAdditionalPolicies && (
                <>
                  <Divider borderBottomWidth="3px" mt={2} />
                  <Box maxH="175px" mb={8} mt={4} overflowY="auto" pr={5}>
                    {additionalPolicyTypes.map(type => (
                      <Box key={type.id} my={4}>
                        <Checkbox
                          data-cy={CypressIds.certDocAddAdditionalPolicies(type.id)}
                          value={type.id}
                          onChange={e => {
                            updateLinkedPolicyTypes(type.id, e.target.checked);

                            if (e.target.checked) {
                              dispatch(blueprint.addAdditionalInsurancePolicyType(type));
                            } else {
                              dispatch(blueprint.removeAdditionalInsurancePolicyType(type));
                            }
                          }}
                        >
                          {type.displayName}
                        </Checkbox>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Box>

            <Box mt={8}>
              <HStack justifyContent="space-between">
                <Button
                  variant="secondary"
                  onClick={() => dispatch(hideModal())}
                >
                  Cancel
                </Button>
                <Button
                  data-analytics-id="confirm-review-document"
                  data-cy={CypressIds.certDocSubmitButton}
                  dusk={CertSubmissionDuskIds.saveDocumentSelectionButton}
                  textTransform="none"
                  variant="gradientPrimary"
                  onClick={handleSubmit(data => {
                    let checkedData = checkedDocTypeIds;

                    if (checkedDocTypeIds.length === 0) {
                      return setDocTypeError('You must select a document type');
                    }

                    if (checkedDocTypeIds.length > 0 && showOtherInput && !Object.keys(otherInputValue).length) {
                      return setDocTypeError('You must select or deselect an Other document type');
                    }

                    if (showOtherInput && !!Object.keys(otherInputValue).length) {
                      checkedData = checkedDocTypeIds.map(id => {
                        if (id === 'other') {
                          return data.other_document_type.id;
                        }

                        return id;
                      });
                    }

                    dispatch(blueprint.prepareDocument(file, checkedData, checkedPolicyTypeIds));
                    return dispatch(hideModal());
                  })}
                >
                  Save Selection(s)
                </Button>
              </HStack>
            </Box>
          </Box>

          {/* Right Side */}
          <Box
            bgColor={colors.backgroundScale['50']}
            borderRadius="md"
            flexBasis="60%"
            p="2.5rem"
          >
            <DocumentViewer file={file} />
          </Box>
        </Flex>
      </form>
    </Box>
  );
};

ReviewDocumentModal.propTypes = { file: PropTypes.object.isRequired, blueprint: CertificateCreationBlueprintType };

export default ReviewDocumentModal;
